import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import JobCard from './JobCard';
import careerService from '../../api/careerService';

// Import fallback image in case API image fails to load
import fallbackImage from '../../assets/images/join_our_team.webp';

const CurrentOpenings = ({ onApplyClick }) => {
  // Create ref for heading
  const headingRef = useRef(null);
  
  // State for job openings data
  const [jobOpenings, setJobOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Function to construct complete image URL
  const constructImageUrl = (imagePath) => {
    if (!imagePath || imagePath === fallbackImage) {
      return fallbackImage;
    }
    
    // If it's already a complete URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('data:')) {
      return imagePath;
    }
    
    // If it's a relative path, construct the full URL
    // Use the same base URL as the API client
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${baseUrl}/${imagePath}`;
  };
  
  // Function to fetch and format job openings
  const fetchJobOpenings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await careerService.getAllCareers(1, 100); // Get all jobs without pagination
      
      // Process the response data
      if (response.data && response.data.data) {
        // Map API response to our component's expected format
        const formattedJobs = response.data.data.map((job, index) => {
          // Handle image properly for each job
          let jobImage = fallbackImage;
          
          
          if (job.images) {
            if (Array.isArray(job.images)) {
              // If it's an array, take the first image
              const rawImage = job.images[0] || fallbackImage;
              jobImage = constructImageUrl(rawImage);
            } else if (typeof job.images === 'string') {
              // If it's a string, use it directly
              const rawImage = job.images;
              jobImage = constructImageUrl(rawImage);
            }
          }
          
          return {
            id: job._id || job.id,
            title: job.product_name || 'Job Position',
            location: job.location || 'Remote',
            description: job.description || 'No description available',
            image: jobImage,
            fullTime: job.time === 'Full-time'
          };
        });
        
        setJobOpenings(formattedJobs);
      } else {
        setJobOpenings([]);
      }
    } catch (err) {
      console.error('Error fetching job openings:', err);
      
      // Provide more specific error messages based on the error type
      if (err.code === 'ECONNABORTED' || !navigator.onLine) {
        setError('Network connection issue. Please check your internet connection and try again.');
      } else if (err.response) {
        // Server responded with an error status
        if (err.response.status === 404) {
          setError('Job listings are currently unavailable. Our team has been notified and is working on it.');
        } else if (err.response.status >= 500) {
          setError('We\'re experiencing technical difficulties with our job listings. Please try again in a few minutes.');
        } else {
          setError('Unable to load job openings. Please refresh the page or try again later.');
        }
      } else {
        setError('Something went wrong while loading job opportunities. Please try again or contact support if the problem persists.');
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch job openings on component mount
  useEffect(() => {
    fetchJobOpenings();
  }, [fetchJobOpenings]);
  

  return (
    <section 
      className="px-4 md:px-20 lg:pl-[80px] lg:pr-[84px] py-[60px] md:py-[80px] bg-[#F9F9FB] overflow-hidden"
    >
      <div className="max-w-[1280px] mx-auto">
        <h2 
          ref={headingRef} 
          className="font-[LTWave] font-medium text-3xl md:text-4xl lg:text-[44px] leading-[150%] mb-8 md:mb-[40px] lg:mb-[50px] will-change-transform animated-item"
        >
          Current Openings
        </h2>
        
        {/* Loading State - Skeleton Loader */}
        {loading && (
          <div className="flex flex-col gap-6">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="job-card will-change-transform">
                <div className="w-full max-w-[1280px] flex flex-col md:flex-row bg-white rounded-[24px] border border-gray-200 overflow-hidden mb-6 shadow-sm animate-pulse">
                  {/* Skeleton Image */}
                  <div className="w-full md:w-[526px] h-[250px] md:h-[363px] md:ml-auto md:order-2 overflow-hidden bg-gray-200 rounded-[15.95px]"></div>
                  
                  {/* Skeleton Content */}
                  <div className="w-full md:w-[614px] pt-[28px] p-[12px] md:p-[35px] flex flex-col gap-4 md:gap-[30px] md:order-1">
                    <div>
                      <div className="h-8 bg-gray-200 rounded-full w-3/4 mb-[12px]"></div>
                      <div className="h-4 bg-gray-200 rounded-full w-1/2"></div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded-full w-full"></div>
                      <div className="h-4 bg-gray-200 rounded-full w-full"></div>
                      <div className="h-4 bg-gray-200 rounded-full w-3/4"></div>
                    </div>
                    
                    <div>
                      <div className="h-[44px] bg-gray-300 rounded-[21.26px] w-[111.64px] mt-4 md:mt-0"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Error State - Improved UI */}
        {!loading && error && (
          <div className="bg-white rounded-[24px] border border-gray-200 p-6 md:p-8 text-center shadow-sm">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-50 text-red-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              <h3 className="text-xl font-medium text-gray-900">Unable to Load Job Openings</h3>
              
              <p className="text-gray-600 max-w-md">{error}</p>
              
              <button 
                onClick={fetchJobOpenings}
                className="mt-4 px-6 py-2 bg-black text-white font-medium rounded-full hover:bg-gray-800 transition-colors"
                aria-label="Try loading job openings again"
              >
                Try Again
              </button>
              
              <p className="text-sm text-gray-500 mt-2">
                You can also <Link to="/contact" className="text-blue-600 hover:underline">contact us</Link> for assistance.
              </p>
            </div>
          </div>
        )}
        
        {/* Job Listings - Original Design */}
        <div
          className="flex flex-col gap-6"
          stagger={0.2}
          ease="power2.out"
          start="top 70%"
          tag="div"
        >
          {!loading && !error && jobOpenings.length > 0 ? (
            jobOpenings.map(job => (
              <div key={job.id} className="job-card will-change-transform">
                <JobCard 
                  id={job.id}
                  title={job.title}
                  location={job.location}
                  description={job.description}
                  image={job.image}
                  fullTime={job.fullTime}
                  onApplyClick={onApplyClick}
                />
              </div>
            ))
          ) : !loading && !error ? (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <div className="bg-[#F5F5F5] rounded-lg p-8 text-center max-w-md w-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 20h.01M12 14h.01M12 4a8 8 0 100 16 8 8 0 000-16z" />
                </svg>
                <h3 className="text-xl font-bold text-gray-800 mb-2">No Job Openings</h3>
                <p className="text-gray-600">We don't have any job openings available at the moment. Please check back later or subscribe to our newsletter for updates.</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

CurrentOpenings.propTypes = {
  onApplyClick: PropTypes.func.isRequired
};

export default CurrentOpenings;
